import React, { useState } from "react"
import { DateTime } from "luxon"
import { Button } from "@mui/material"
import HorizontalSchedule, { type BusyBlock } from "./horizontal_schedule"
import i18n from "@/i18n"
import type { ReservationStatus } from "@/constants/reservations"

interface HorizontalScheduleWithActionsProps {
  timeOptions: [string, DateTime][]
  busyBlocks: BusyBlock[]
  currentReservationStatus?: ReservationStatus
  internalStartTime: DateTime | null
  internalEndTime: DateTime | null
  parentStartTime: DateTime | string | null
  parentEndTime: DateTime | string | null
  onTimeChange: (startTime: DateTime, endTime: DateTime) => void
  onSave: () => void
  onCancel: () => void
  hasDate: boolean
  hasHighlightedTable: boolean
  showParentTimes: boolean
  hasPendingChanges: boolean
  reservationBuffer?: number
  mode?: "edit" | "add"
  scrollToStartTime?: DateTime | null
  scrollToEndTime?: DateTime | null
}

const HorizontalScheduleWithActions: React.FC<HorizontalScheduleWithActionsProps> = ({
  timeOptions,
  busyBlocks,
  currentReservationStatus,
  internalStartTime,
  internalEndTime,
  parentStartTime,
  parentEndTime,
  onTimeChange,
  onSave,
  onCancel,
  hasDate,
  hasHighlightedTable,
  showParentTimes,
  hasPendingChanges,
  reservationBuffer = 0,
  mode = "edit",
  scrollToStartTime = null,
  scrollToEndTime = null,
}) => {
  const [hasConflicts, setHasConflicts] = useState(false)

  const normalizeTime = (time: DateTime | string): DateTime => {
    if (typeof time === "string") {
      return DateTime.fromISO(time)
    }
    return time
  }

  const hasInternalTimes = internalStartTime && internalEndTime
  const hasParentTimes = parentStartTime && parentEndTime

  let currentReservation = undefined
  if (hasInternalTimes) {
    currentReservation = {
      startTime: internalStartTime,
      endTime: internalEndTime,
      status: currentReservationStatus,
    }
  } else if (hasParentTimes && showParentTimes) {
    currentReservation = {
      startTime: normalizeTime(parentStartTime),
      endTime: normalizeTime(parentEndTime),
      status: currentReservationStatus,
    }
  }

  const showReservationBlock = currentReservation && (hasHighlightedTable || hasInternalTimes)

  const saveButtonLabel =
    mode === "add"
      ? i18n.t("app.reservation_plan_selector.add_table")
      : i18n.t("app.reservation_plan_selector.save_table")

  return (
    <div className="flex flex-col gap-2" data-testid="schedule-actions-container">
      {!hasDate && (
        <div className="bg-white border border-[#e5e6ec] rounded-[5px] p-4 text-center">
          <span className="text-[#5f6774] text-sm font-medium">
            {i18n.t("app.reservation_plan_selector.select_date_first")}
          </span>
        </div>
      )}
      {hasDate && (
        <>
          <HorizontalSchedule
            timeOptions={timeOptions}
            busyBlocks={busyBlocks}
            currentReservation={showReservationBlock ? currentReservation : undefined}
            onTimeChange={onTimeChange}
            onConflictChange={setHasConflicts}
            reservationBuffer={reservationBuffer}
            disabled={!hasDate}
            scrollToStartTime={scrollToStartTime}
            scrollToEndTime={scrollToEndTime}
          />
          <div className="flex items-center gap-2 px-4">
            <Button
              variant="outlined"
              size="small"
              onClick={onCancel}
              disabled={!hasPendingChanges}
              data-testid="schedule-actions-cancel"
              sx={{
                borderColor: "#e5e7eb",
                color: "#5f6774",
                borderRadius: "5px",
                textTransform: "none",
                fontSize: "12px",
                "&:hover": { borderColor: "#2596fa", color: "#2596fa" },
              }}
            >
              {i18n.t("app.reservation_plan_selector.cancel")}
            </Button>
            <Button
              variant="contained"
              size="small"
              className="flex-1"
              onClick={onSave}
              disabled={!hasPendingChanges || hasConflicts}
              data-testid="schedule-actions-save"
              sx={{
                backgroundColor: "#2596fa",
                borderRadius: "5px",
                textTransform: "none",
                fontSize: "12px",
                "&:hover": { backgroundColor: "#1e7cd8" },
                "&.Mui-disabled": { backgroundColor: "#2596fa", opacity: 0.5 },
              }}
            >
              {saveButtonLabel}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default HorizontalScheduleWithActions
